import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";

/**
 * Compact AR summary strip — 4 read-only metric tiles.
 * Consumes: GET /finance/reports/ar-summary
 */
export default function ArSummaryStrip() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/finance/reports/ar-summary")
      .then(({ data }) => setD(data))
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  if (loading || !d) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="ar-summary-loading">
        {[0,1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"/>)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="ar-summary-strip">
      <Tile testId="ar-summary-outstanding" label="Total outstanding"
            value={fmtMoney(d.total_outstanding_cents)}
            hint={`${d.customers_with_balance} customer(s)`}/>
      <Tile testId="ar-summary-overdue" label="Overdue"
            value={fmtMoney(d.overdue_cents)}
            tone={d.overdue_cents > 0 ? "danger" : "neutral"}/>
      <Tile testId="ar-summary-over-facility" label="Over facility"
            value={String(d.over_facility_count)}
            tone={d.over_facility_count > 0 ? "danger" : "neutral"}
            hint="customers"/>
      <Tile testId="ar-summary-unallocated" label="Unallocated credit"
            value={fmtMoney(d.unallocated_credit_cents)}
            tone={d.unallocated_credit_cents > 0 ? "good" : "neutral"}/>
    </div>
  );
}

function Tile({ label, value, hint, tone = "neutral", testId }) {
  const tones = {
    neutral: "border-slate-200 text-slate-900",
    danger: "border-red-300 bg-red-50 text-red-800",
    good: "border-green-300 bg-green-50 text-green-800",
  };
  return (
    <div className={"border rounded p-3 " + tones[tone]} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono font-bold text-xl leading-tight">{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}
